export type SelectedOptionsMap = Record<string, string[]>;
export type InputValuesMap = Record<string, string>;

export function uniqueOptionIds(optionIds: string[] | undefined): string[] {
  if (!optionIds) {
    return [];
  }

  const seen = new Set<string>();
  const unique: string[] = [];

  optionIds.forEach((optionId) => {
    if (seen.has(optionId)) {
      return;
    }
    seen.add(optionId);
    unique.push(optionId);
  });

  return unique;
}

export function normalizeSelectedOptions(selectedOptions?: SelectedOptionsMap | null): SelectedOptionsMap {
  if (!selectedOptions) {
    return {};
  }

  return Object.entries(selectedOptions).reduce<SelectedOptionsMap>((normalized, [groupId, optionIds]) => {
    const clean = (Array.isArray(optionIds) ? optionIds : [])
      .filter((optionId): optionId is string => typeof optionId === 'string' && optionId.length > 0);
    if (clean.length > 0) {
      normalized[groupId] = clean;
    }
    return normalized;
  }, {});
}

export function normalizeSelectedOptionsForPayload(
  selectedOptions?: SelectedOptionsMap | null,
): SelectedOptionsMap | undefined {
  const normalized = normalizeSelectedOptions(selectedOptions);
  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

export function areSelectedOptionsEqual(
  left?: SelectedOptionsMap | null,
  right?: SelectedOptionsMap | null,
): boolean {
  const leftNormalized = normalizeSelectedOptions(left);
  const rightNormalized = normalizeSelectedOptions(right);
  const leftKeys = Object.keys(leftNormalized).sort();
  const rightKeys = Object.keys(rightNormalized).sort();

  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  return leftKeys.every((groupId, index) => {
    if (groupId !== rightKeys[index]) {
      return false;
    }

    const leftOptions = [...leftNormalized[groupId]].sort();
    const rightOptions = [...rightNormalized[groupId]].sort();

    if (leftOptions.length !== rightOptions.length) {
      return false;
    }

    return leftOptions.every((optionId, optionIndex) => optionId === rightOptions[optionIndex]);
  });
}

export function normalizeInputValues(inputValues?: InputValuesMap | null): InputValuesMap {
  if (!inputValues) {
    return {};
  }

  return Object.entries(inputValues).reduce<InputValuesMap>((normalized, [groupId, value]) => {
    if (typeof value !== 'string') {
      return normalized;
    }

    const trimmed = value.trim();
    if (trimmed) {
      normalized[groupId] = trimmed;
    }
    return normalized;
  }, {});
}

export function areInputValuesEqual(
  left?: InputValuesMap | null,
  right?: InputValuesMap | null,
): boolean {
  const leftNormalized = normalizeInputValues(left);
  const rightNormalized = normalizeInputValues(right);
  const leftKeys = Object.keys(leftNormalized).sort();
  const rightKeys = Object.keys(rightNormalized).sort();

  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  return leftKeys.every((groupId, index) =>
    groupId === rightKeys[index] && leftNormalized[groupId] === rightNormalized[groupId],
  );
}
